import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ShoppingBag, MessageCircle, Smartphone, ChevronRight, Sparkles, RefreshCw } from 'lucide-react';
import { usePageView } from '../hooks/useAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Track page view
  usePageView('home');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch all home data in one call
      const homeRes = await fetch(`${API_URL}/api/public/home`);
      
      if (homeRes.ok) {
        const data = await homeRes.json();
        setSettings(data.settings);
        setCategories(data.categories || []);
        setFeaturedProducts(data.featured_products || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Calcula o menor preço de um produto (considerando variações)
  const getMinPrice = (product) => {
    let productVariants = product.product_variants;
    if (typeof productVariants === 'string') {
      try {
        productVariants = JSON.parse(productVariants);
      } catch {
        productVariants = null;
      }
    }

    if (productVariants?.skus?.length > 0) {
      const availableSkus = productVariants.skus.filter(sku => sku.available !== false);
      if (availableSkus.length > 0) {
        const prices = availableSkus.map(sku => sku.price).filter(p => p > 0);
        if (prices.length > 0) {
          return { price: Math.min(...prices), hasVariants: true };
        }
      }
    }

    return { price: product.price_pix || product.price || 0, hasVariants: false };
  };

  // Default values from settings
  const hero = {
    badge: settings?.hero_badge || "TECNOLOGIA PREMIUM",
    title: settings?.hero_title || "Smartphones de Elite",
    subtitle: settings?.hero_subtitle || "Curadoria exclusiva dos melhores celulares do mercado.",
    image_url: settings?.hero_image_url || "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=500&q=80",
    cta_text: settings?.hero_cta_text || "Ver Ofertas Premium",
    cta_link: settings?.hero_cta_link || "/catalogo"
  };

  const editableSection = {
    is_active: settings?.home_after_brands_enabled || false,
    title: settings?.home_after_brands_title || "",
    text: settings?.home_after_brands_text || ""
  };

  // Icon mapping
  const getIcon = (iconName, color) => {
    const iconProps = { className: `h-6 w-6 md:h-7 md:w-7 transition-colors`, style: { color } };
    switch (iconName) {
      case 'RefreshCw': return <RefreshCw {...iconProps} />;
      case 'Sparkles': return <Sparkles {...iconProps} />;
      default: return <Smartphone {...iconProps} />;
    }
  };

  // Format categories for display
  const brandsSection = {
    title: "Marcas Consolidadas",
    subtitle: "Encontre seu próximo smartphone pela marca",
    items: categories.map(cat => ({
      key: cat.slug,
      label: cat.name,
      description: cat.description,
      icon: cat.icon_name,
      color: cat.icon_color,
      filter_query: cat.slug === 'seminovos' ? 'condition=seminovo' : 
                    cat.slug === 'acessorios' ? 'category=acessorios' : 
                    `brand=${cat.slug}`,
      order: cat.sort_order,
      is_active: cat.is_active
    }))
  };

  const getWhatsAppNumber = () => {
    return settings?.whatsapp_number || '5543996963570';
  };

  const handleWhatsAppClick = (message) => {
    const phoneNumber = getWhatsAppNumber();
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#0F0F10]">
      
      {/* HERO BANNER */}
      <section className="relative bg-gradient-to-br from-[#1A1A1D] via-[#0F0F10] to-[#1A1A1D] overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-[#C9A24D] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C9A24D] opacity-5 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-10 md:py-16">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-center md:text-left">
              <span className="inline-block bg-[#C9A24D]/10 border border-[#C9A24D]/30 text-[#C9A24D] px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
                {hero.badge}
              </span>
              <h1 className="text-cinzel text-3xl md:text-5xl font-bold text-[#EAEAEA] mb-4 leading-tight">
                {hero.title.split(' ').map((word, i) => 
                  i === hero.title.split(' ').length - 1 
                    ? <span key={i} className="text-[#C9A24D]">{word}</span>
                    : <span key={i}>{word} </span>
                )}
              </h1>
              <p className="text-[#B5B5B5] text-base md:text-lg max-w-md mx-auto md:mx-0">
                {hero.subtitle}
              </p>
            </div>
            
            <div className="flex-1 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#C9A24D] opacity-20 blur-3xl rounded-full"></div>
                <img 
                  src={hero.image_url} 
                  alt="Smartphones Premium"
                  className="relative w-64 md:w-80 h-auto object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUTOS EM DESTAQUE - Grid 2x2 */}
      <section className="py-8 md:py-12 bg-[#0F0F10]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-cinzel text-xl md:text-3xl font-bold text-[#EAEAEA] tracking-wider">
              Produtos em Destaque
            </h2>
            <div className="w-16 h-0.5 bg-[#C9A24D] mx-auto mt-2"></div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-[#1C1C1F] rounded-xl h-64 md:h-80 animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
              {featuredProducts.map((product) => (
                <div
                  key={product.id}
                  className="group bg-[#1C1C1F] rounded-xl overflow-hidden border border-[#2A2A2D] hover:border-[#C9A24D] transition-all duration-300"
                  data-testid={`featured-product-${product.id}`}
                >
                  <Link to={`/produto/${product.id}`} className="block">
                    <div className="relative h-32 md:h-44 bg-[#0F0F10] overflow-hidden">
                      {(product.image_url || product.images?.[0]) ? (
                        <img 
                          src={product.image_url || product.images[0]} 
                          alt={product.title || product.model} 
                          className="w-full h-full object-contain p-3 md:p-4 group-hover:scale-105 transition-transform duration-300" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#3A3A3D]">
                          <ShoppingBag className="h-12 w-12" />
                        </div>
                      )}
                    </div>
                  </Link>

                  <div className="p-3 md:p-4">
                    <Link to={`/produto/${product.id}`}>
                      <h3 className="text-[#EAEAEA] font-semibold text-sm md:text-base truncate mb-0.5">
                        {product.title || `${product.brand} ${product.model}`}
                      </h3>
                    </Link>
                    
                    <div className="bg-[#0F0F10] border border-[#C9A24D]/30 rounded-lg p-2 md:p-3 my-2">
                      {(() => {
                        const { price, hasVariants } = getMinPrice(product);
                        return (
                          <>
                            {hasVariants && (
                              <p className="text-[#B5B5B5] text-[10px] uppercase tracking-wider mb-0.5">
                                A partir de
                              </p>
                            )}
                            <p className="text-[#C9A24D] font-bold text-lg md:text-xl">
                              {formatPrice(price)}
                            </p>
                            <p className="text-[#B5B5B5] text-[10px] md:text-xs uppercase tracking-wider">
                              no PIX
                            </p>
                          </>
                        );
                      })()}
                    </div>

                    <button
                      onClick={() => handleWhatsAppClick(`Quero garantir o ${product.title || product.brand + ' ' + product.model}`)}
                      className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-2 md:py-2.5 rounded-lg font-bold text-xs md:text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-1.5"
                      data-testid={`buy-btn-${product.id}`}
                    >
                      <Phone className="h-3.5 w-3.5 md:h-4 md:w-4" />
                      Comprar Agora
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* BOTÃO VER TODAS */}
      <section className="py-4 md:py-6 bg-[#0F0F10]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Link
            to="/catalogo"
            className="inline-block bg-transparent border-2 border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D] hover:text-[#0F0F10] px-8 py-3 rounded-lg font-bold uppercase tracking-wider transition-all text-sm md:text-base"
            data-testid="view-all-offers-btn"
          >
            Ver Todas as Ofertas
          </Link>
        </div>
      </section>

      {/* MARCAS CONSOLIDADAS */}
      <section className="py-8 md:py-12 bg-[#0F0F10]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-cinzel text-xl md:text-3xl font-bold text-[#EAEAEA] tracking-wider">
              {brandsSection.title}
            </h2>
            <div className="w-16 h-0.5 bg-[#C9A24D] mx-auto mt-2 mb-3"></div>
            <p className="text-[#B5B5B5] text-sm md:text-base">
              {brandsSection.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {(brandsSection.items || [])
              .filter(item => item.is_active !== false)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((brand) => (
                <Link
                  key={brand.key}
                  to={`/catalogo?${brand.filter_query}`}
                  className="group bg-[#1C1C1F] border border-[#2A2A2D] hover:border-[#C9A24D] rounded-xl p-4 md:p-5 transition-all duration-300 hover:shadow-[0_4px_20px_rgba(201,162,77,0.15)]"
                  data-testid={`brand-card-${brand.key}`}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-[#0F0F10] rounded-full flex items-center justify-center mb-3 border border-[#3A3A3D] group-hover:border-[#C9A24D] transition-colors">
                      {getIcon(brand.icon, brand.color)}
                    </div>
                    <h3 className="text-cinzel font-bold text-[#EAEAEA] text-base md:text-lg mb-1 group-hover:text-[#C9A24D] transition-colors">
                      {brand.label}
                    </h3>
                    <p className="text-[#B5B5B5] text-xs leading-tight">
                      {brand.description}
                    </p>
                    <ChevronRight className="h-4 w-4 text-[#C9A24D] mt-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* SEÇÃO EDITÁVEL (após marcas) */}
      {editableSection.is_active && (
        <section className="py-8 md:py-12 bg-[#1C1C1F]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-cinzel text-xl md:text-2xl font-bold text-[#EAEAEA] mb-4">
              {editableSection.title}
            </h2>
            <p className="text-[#B5B5B5] text-sm md:text-base leading-relaxed">
              {editableSection.text}
            </p>
          </div>
        </section>
      )}

      {/* BOTÃO FLUTUANTE WHATSAPP */}
      <div className="md:hidden">
        <a
          href={`https://wa.me/${settings?.footer?.whatsapp || '5543996963570'}?text=Olá! Vim pelo site da MULTICELL.`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-3 rounded-full shadow-2xl flex items-center justify-center"
          data-testid="floating-whatsapp-btn"
          aria-label="WhatsApp"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="h-9 w-9 fill-white">
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
          </svg>
        </a>
      </div>
    </div>
  );
};

export default Home;
